import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import {
    AuthCredentials,
    RefreshTokenRequest,
    TokenResponse,
    TokenResponseTransformed,
} from 'app/core/auth/auth.types';
import { UserService } from 'app/core/user/user.service';
import { catchError, Observable, of, switchMap, tap } from 'rxjs';
import { RequestService } from '../services/request.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private _requestService = inject(RequestService);
    private _userService = inject(UserService);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        if (token && token !== 'undefined' && token !== 'null') {
            localStorage.setItem('accessToken', token);
        }
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    /**
     * Setter & getter for refresh token
     */
    set refreshToken(token: string) {
        if (token && token !== 'undefined' && token !== 'null') {
            localStorage.setItem('refreshToken', token);
        }
    }

    get refreshToken(): string {
        return localStorage.getItem('refreshToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Sign in with email and password
     * Flow:
     * 1. Call POST /auth/token with credentials
     * 2. Transform response from snake_case to camelCase
     * 3. Save tokens to localStorage
     * 4. Call signInUsingToken() to load user and validate
     *
     * @param credentials - Email and password
     */
    signIn(credentials: AuthCredentials): Observable<boolean> {
        const params = new HttpParams()
            .set('username', credentials.email)
            .set('password', credentials.password);

        return this._requestService.post<TokenResponse>('/auth/token', params).pipe(
            tap((tokenResponse: TokenResponse) => {
                // Transform snake_case to camelCase and store tokens
                this.accessToken = tokenResponse.access_token;
                this.refreshToken = tokenResponse.refresh_token;
            }),
            switchMap(() => {
                return this.signInUsingToken();
            }),
            catchError((error) => {
                this._authenticated = false;
                throw error;
            })
        );
    }

    /**
     * Sign in using token
     * Validates token by calling GET /auth/me and stores user
     * _authenticated is set to true AFTER getting user data
     */
    signInUsingToken(): Observable<boolean> {
        if (!this.accessToken || AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // Get user data from /auth/me (validates token)
        return this._requestService.get<any>('/auth/me').pipe(
            tap((authUser: any) => {
                // Store user in UserService
                this._userService.user = authUser;

                // Set authenticated
                this._authenticated = true;
            }),
            switchMap(() => of(true)),
            catchError(() => {
                this._authenticated = false;
                return of(false);
            })
        );
    }

    /**
     * Sign out (legacy method for compatibility)
     *
     * Flow:
     * 1. Call POST /auth/logout with refreshToken
     * 2. Remove tokens from localStorage
     * 3. Clear user BehaviorSubject
     * 4. Set authenticated to false
     */
    signOut(): Observable<void> {
        const request: RefreshTokenRequest = {
            refreshToken: this.refreshToken,
        };

        return this._requestService.post<void>('/auth/logout', request).pipe(
            switchMap(() => {
                return this._clearAuthData();
            }),
            catchError(() => {
                // Even if logout call fails, clear local data
                return this._clearAuthData();
            })
        );
    }

    /**
     * Refresh access token
     * Call POST /auth/refresh with refresh token
     *
     * @returns Observable<TokenResponseTransformed>
     */
    refreshAccessToken(): Observable<TokenResponseTransformed> {
        const request: RefreshTokenRequest = {
            refreshToken: this.refreshToken,
        };

        return this._requestService.post<TokenResponse>('/auth/refresh', request).pipe(
            tap((response: TokenResponse) => {
                // Transform and update tokens
                this.accessToken = response.access_token;
                this.refreshToken = response.refresh_token;
            }),
            switchMap((response: TokenResponse) => {
                // Return transformed response
                return of({
                    accessToken: response.access_token,
                    refreshToken: response.refresh_token,
                    tokenType: response.token_type,
                    expiresIn: response.expires_in,
                });
            }),
            catchError((error) => {
                // If refresh fails, logout
                this.signOut();
                throw error;
            })
        );
    }

    /**
     * Check authentication status
     * Verifies if user has an access token (even if close to expiration)
     *
     * The guard uses this to allow access to protected routes.
     * The interceptor will handle 401 responses and refresh if needed.
     * This allows the refresh token flow to work seamlessly.
     */
    check(): Observable<boolean> {
        // Check the access token availability
        if (!this.accessToken) {
            this._authenticated = false;
            return of(false);
        }

        // If token exists, allow access (even if close to expiration)
        // The interceptor will refresh if the server responds with 401
        if (this._authenticated) {
            return of(true);
        }

        // If the access token exists but we haven't validated it yet, do so now
        return this.signInUsingToken();
    }

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        const body = { email: email };
        return this._requestService.post('/auth/forgot-password', body);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(token: string, password: string): Observable<any> {
        const body = { token: token, new_password: password };
        return this._requestService.post('/auth/reset-password', body);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
        return this._requestService.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._requestService.post('api/auth/unlock-session', credentials);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Clear authentication data
     * Remove tokens from localStorage
     *
     * @private
     */
    private _clearAuthData(): Observable<void> {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        this._authenticated = false;
        return of(void 0);
    }
}
